import React from "react";
import {
  createFileRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import {
  Badge,
  Box,
  BreadcrumbItem,
  Button,
  EmptyState,
  Separator,
  Tabs,
  TabsList,
  TabsTab,
  TabsViewIcon,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type TabsViewIconProps,
} from "@salestrak/ui";
import { PageHeaderBreadcrumb } from "#app/components/page-header";
import { TeamAddMemberAction } from "#app/components/teams/team-add-member-action";
import { TeamBreadcrumbMenu } from "#app/components/teams/team-breadcrumb-menu";
import { withMemberModule } from "#app/components/permissions/require-permission";
import { VIEW_PANE_ANCHOR_ID } from "#app/lib/hooks/use-anchor-insets";
import { IssueFilterProvider } from "#app/lib/filters/issue-filter-context";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useTeamDetail } from "#app/lib/hooks/use-team-detail";
import { formatTeamHandle } from "#app/lib/team-handle";
import { useCopyFeedback } from "#app/lib/utils/copy-actions";
import {
  TEAM_STRIP_TABS,
  type TeamViewTab,
} from "#app/lib/hooks/use-view-tab-order";
import { useRememberLastView } from "#app/lib/views/last-view";
import { ViewsProvider, useViews } from "#app/lib/views/view-context";
import { useAnalytics, AnalyticsContext, IssueEvent, AnalyticsScope, TeamEvent } from "#app/lib/analytics";
import { EntityShellSkeleton } from "#app/components/loading/entity-shell-skeleton";

/**
 * Single-team chrome wrapping the view routes (`/overview` — the landing tab
 * — `/members`, `/chart`, `/board` and `/list`): the breadcrumb masthead, the
 * view tabs, an object-level access guard,
 * and the shared filter context + filter-panel overlay. The header renders
 * here once, above `<Outlet />`, so it is identical across all views. Each
 * route renders its own body and its own toolbar band below the tab strip.
 *
 * The masthead's right end seats Add member: the team's one primary action,
 * for `viewerCanManage` holders, on EVERY tab — membership is a property of
 * the team, not of the roster view. A control specific to one view (the
 * Overview's Manage cards) stays in that view's own toolbar band.
 */

// Team tabs seat their glyphs as cut-outs (`ink="ground"`): the tile reads
// as a mark on the page rather than a badge, which is the team page's
// grammar. Workflow view tabs keep the default white glyph.
// Overview, Projects, Members and Team Chart are the team's STATIC anchors —
// fixed identity, fixed seat, never dragged. There is deliberately no
// custom-view tier on the team page: the anchors are the whole strip, so the
// row has nothing to reorder and no rail hairline to draw.
const TAB_META: Record<
  (typeof TEAM_STRIP_TABS)[number],
  {
    icon: TabsViewIconProps["icon"];
    color: TabsViewIconProps["color"];
    label: string;
  }
> = {
  overview: { icon: "home-filled", color: "grey", label: "Overview" },
  // The `list` route, named for what it holds — accounts and contacts can sit
  // beside it later without a "List" tab going ambiguous.
  list: { icon: "view-list", color: "orange", label: "Projects" },
  members: { icon: "users-filled", color: "purple", label: "Members" },
  // Same glyph as the workspace Org Chart nav row — one visual language for
  // the same canvas concept.
  chart: { icon: "network", color: "green", label: "Team Chart" },
};

/** The tab the URL names, or null on the team's own index (no tab yet). */
function useActiveTabSegment(): TeamViewTab | null {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastSegment = pathname.split("/").pop();
  if (lastSegment === "overview") return "overview";
  if (lastSegment === "members") return "members";
  if (lastSegment === "chart") return "chart";
  if (lastSegment === "board") return "board";
  if (lastSegment === "list") return "list";
  return null;
}

/**
 * The team's saved-view scope: its record tabs (Projects, the `list` route)
 * are built-in team views, so the Customize view drawer edits their columns,
 * filters and grouping and saves them for everyone. The roster tabs and the
 * Overview are not views; on them `activeView` is null.
 */
function TeamLayout() {
  const { teamId } = Route.useParams();
  const activeSegment = useActiveTabSegment();
  const scope = React.useMemo(
    () => ({ kind: "team" as const, id: teamId }),
    [teamId],
  );
  return (
    <ViewsProvider
      key={teamId}
      scope={scope}
      activeKey={activeSegment === "list" ? "list" : null}
    >
      <TeamFilterScope>
        <TeamLayoutInner />
      </TeamFilterScope>
    </ViewsProvider>
  );
}

/** The filter set, keyed per view like a workflow's: inside a team view the
 *  provider binds to that view's config draft. */
function TeamFilterScope({ children }: { children: React.ReactNode }) {
  const { activeView } = useViews();
  return (
    <IssueFilterProvider key={activeView?.id ?? ""} analyticsScope={AnalyticsScope.Team}>
      {children}
    </IssueFilterProvider>
  );
}

function TeamLayoutInner() {
  const { teamId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { track } = useAnalytics();
  const { teamMap, isHydrated } = useWorkspaceData();

  const team = teamId ? teamMap.get(teamId) : undefined;
  // `viewerCanManage` rides the workspace team map (batched on boot), so the
  // gate costs no extra query. The detail query supplies the picker's
  // exclusion set (leads + members) — the same normalized entity the roster
  // reads, so an add here is visible to the Members tab through the cache.
  // It also runs for a team MISSING from the map: `team(id:)` resolves any
  // team that exists, so a null there tells a deleted team (or a wrong link)
  // from a private one the viewer is not on.
  const canManage = team?.viewerCanManage === true;
  const missing = isHydrated && !team;
  const { team: teamDetail, loading: teamDetailLoading } = useTeamDetail(teamId, {
    skip: !canManage && !missing,
  });
  const existingUserIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          [...(teamDetail?.leads ?? []), ...(teamDetail?.members ?? [])].map(
            (u) => u.userId,
          ),
        ),
      ),
    [teamDetail?.leads, teamDetail?.members],
  );

  // The handle is an address, so a press copies it — the way a mention is
  // pasted somewhere — rather than opening anything. The tooltip is the
  // in-place feedback: "Copy" until the press lands, "Copied" for a beat.
  const { copied, copy: copyText } = useCopyFeedback();

  const activeSegment = useActiveTabSegment();
  const activeTab: TeamViewTab = activeSegment ?? "overview";
  // The board has no tab of its own: it is the team's Projects shown as
  // columns, so the Projects tab stays lit on it.
  const stripTab = activeTab === "board" ? "list" : activeTab;
  // The bare team URL reopens this tab next time. The index route names no
  // tab while it decides where to land, and writes nothing.
  const lastViewScope = React.useMemo(
    () => ({ kind: "team", id: teamId ?? "" }),
    [teamId],
  );
  useRememberLastView(lastViewScope, activeSegment);

  const navigateTab = React.useCallback(
    (tab: string) => {
      if (tab === activeTab) return;
      track({
        context: AnalyticsContext.Issue,
        event: IssueEvent.SwitchTab,
        data: { scope: AnalyticsScope.Team, tab },
      });
      replace(`/team/${teamId}/${tab}`);
    },
    [activeTab, teamId, replace, track],
  );

  // Object-level guard: teams are filtered by visibility, so a team missing
  // from the loaded workspace data means the viewer can't see it (or it doesn't
  // exist). Wait for hydration before deciding so a fresh load isn't denied.
  // The shell's own silhouette, not a spinner: whatever the row says, the
  // destination is this masthead over this strip over a view pane.
  if (!isHydrated || (missing && teamDetailLoading && !teamDetail)) {
    return <EntityShellSkeleton />;
  }

  if (!team) {
    return (
      <Box fill="flex" align="center" justify="center" padding="lg">
        <EmptyState
          media="icon"
          icon={teamDetail ? "lock" : "users"}
          title={teamDetail ? "You do not have access" : "Team not found"}
          description={
            teamDetail
              ? "This team is private. Ask one of its managers or an admin to add you."
              : "This team was deleted, or the link is wrong."
          }
          actions={<Button onClick={() => replace("/teams")}>Back to Teams</Button>}
        />
      </Box>
    );
  }

  const handle = formatTeamHandle(team.teamKey);
  const copyHandle = async () => {
    if (!handle) return;
    if (await copyText(handle, "Handle")) {
      track({ context: AnalyticsContext.Team, event: TeamEvent.CopyHandle, data: { via: "masthead" } });
    }
  };

  return (
    <Box fill="flex" direction="column" minHeight={0} minWidth={0}>
      {/* Same masthead structure and rhythm as the workflow shell: 8px top,
        * 28px first line (the shell-wide contract — see PageHeader), 8px gap,
        * then the 40px underline tab strip — 84px to the separator its active
        * bar rides. The two entity shells must stay visually parallel. */}
      <Box gap="sm" paddingTop="sm" paddingX="md" shrink={false}>
        <Box
          direction="row"
          align="center"
          justify="between"
          gap="sm"
          minHeight={28}
        >
          <Box direction="row" align="center" gap="sm" minWidth={0}>
            <PageHeaderBreadcrumb>
              <BreadcrumbItem>
                {/* The crumb IS the current page, so a press opens the
                    team's management menu (rename in place, color & icon,
                    settings) instead of routing — the chevron says so. The
                    workflow shell's grammar. */}
                <TeamBreadcrumbMenu team={team} />
              </BreadcrumbItem>
            </PageHeaderBreadcrumb>
            {/* The handle wears its @ — it is the address a team is mentioned
                by. A quiet colorless chip beside the name: identity data, not
                a status, so no hue. Edited on the settings page (uniqueness
                validation lives there), never in place. */}
            {handle ? (
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Badge
                      size="tag"
                      render={<Box as="button" type="button" onClick={() => void copyHandle()} />}
                    />
                  }
                >
                  {handle}
                </TooltipTrigger>
                <TooltipPopup side="bottom">{copied ? "Copied" : "Copy"}</TooltipPopup>
              </Tooltip>
            ) : null}
          </Box>
          {/* First-line actions are the 24px rung. */}
          {canManage ? (
            <Box shrink={false}>
              <TeamAddMemberAction
                teamId={teamId}
                existingUserIds={existingUserIds}
              />
            </Box>
          ) : null}
        </Box>

        <Tabs value={stripTab} onValueChange={navigateTab}>
          <TabsList variant="underline">
            {TEAM_STRIP_TABS.map((tab) => (
              <TabsTab key={tab} value={tab}>
                <TabsViewIcon
                  icon={TAB_META[tab].icon}
                  color={TAB_META[tab].color}
                  ink="ground"
                  variant="compact-tile"
                />
                {TAB_META[tab].label}
              </TabsTab>
            ))}
          </TabsList>
        </Tabs>
      </Box>

      {/* Flush under the strip — the active tab's underline bar overlaps this
          hairline, so any padding between them breaks the genre. */}
      <Separator />

      {/* Anchor for the below-tabs overlays — the filter drawer and the
          Overview manage panel both pin to this region so the view tabs stay
          visible above them. */}
      <Box
        id={VIEW_PANE_ANCHOR_ID}
        direction="column"
        fill="flex"
        minHeight={0}
        minWidth={0}
        overflow="hidden"
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/team/$teamId")({
  // A member module: a Guest never reaches it (`ModuleTier`). The rail
  // omits the tile; the route denies for itself since the URL still resolves.
  component: withMemberModule(TeamLayout),
});
